import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  ProgressBar,
  Text,
  Spinner,
  Link,
  MessageBar,
  MessageBarBody,
} from '@coreai/fluentui-react';
import { ArrowSyncRegular, InfoRegular } from '@fluentui/react-icons';
import {
  useSubscriptions,
  useResourceGroups,
  useAutomationProjectLocations,
} from '@/hooks/useAzureResources';
import { SearchableCombobox } from '@/components/shared';
import {
  listAutomationProjects,
  createAutomationProject,
  type AutomationProjectSummary,
} from '@/services/projectService';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuth } from '@/auth';
import { useQuery } from '@tanstack/react-query';
import { CREATE_PROJECT_URL } from '@/constants/links';
import { useStyles } from './CreateProjectDialog.styles';

export type CreateProjectDialogProps = {
  open: boolean;
  onClose: () => void;
  onProjectCreated?: () => void;
};

interface ProvisioningLocation {
  name: string;
  displayName: string;
  regionalDisplayName: string;
}

export function getProvisioningLocationOptions(locations: readonly ProvisioningLocation[] | undefined) {
  return (locations ?? [])
    .filter((location) => location.name.toLowerCase() !== 'swedencentral')
    .map((location) => ({
      value: location.name,
      text: location.displayName,
      secondaryText: location.regionalDisplayName,
      searchTerms: [location.regionalDisplayName],
    }));
}

/**
 * Validates a project name.
 * @param name - The project name to validate
 * @param existingNames - Array of existing project names to check for duplicates
 * @returns Validation result with isValid flag and error message
 */
export const validateProjectName = (name: string, existingNames: string[]): { isValid: boolean; error: string } => {
  const trimmed = name.trim();
  if (!trimmed) {
    return { isValid: false, error: '' }; // Empty - no error message shown yet
  }
  // Check format: alphanumeric, dashes, underscores, 1-64 chars
  const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;
  if (!validNamePattern.test(trimmed)) {
    return {
      isValid: false,
      error: 'Name must start with a letter and contain only letters, numbers, dashes, or underscores (max 64 chars)',
    };
  }
  // Check for duplicate names (case-insensitive)
  const existingNamesLower = existingNames.map((n) => n.toLowerCase());
  if (existingNamesLower.includes(trimmed.toLowerCase())) {
    return { isValid: false, error: `An environment named "${trimmed}" already exists in this subscription` };
  }
  return { isValid: true, error: '' };
};

export const CreateProjectDialog = ({
  open,
  onClose,
  onProjectCreated,
}: CreateProjectDialogProps) => {
  const styles = useStyles();
  const { getArmToken } = useAuth();
  const addNotification = useNotificationStore((s) => s.addNotification);

  // Form state
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
  const [selectedResourceGroup, setSelectedResourceGroup] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [projectName, setProjectName] = useState('');

  // Creation state
  const [isCreating, setIsCreating] = useState(false);
  const [provisioningState, setProvisioningState] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch subscriptions
  const {
    data: subscriptions,
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
    refetch: refetchSubscriptions,
  } = useSubscriptions();

  // Fetch resource groups (enabled when subscription is selected)
  const {
    data: resourceGroups,
    isLoading: resourceGroupsLoading,
    error: resourceGroupsError,
    refetch: refetchResourceGroups,
  } = useResourceGroups(selectedSubscriptionId, !!selectedSubscriptionId);

  // Fetch locations (enabled when subscription is selected)
  const {
    data: locations,
    isLoading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useAutomationProjectLocations(selectedSubscriptionId, !!selectedSubscriptionId);

  // Fetch existing projects for duplicate validation
  const {
    data: existingProjects,
    isLoading: projectsLoading,
    refetch: refetchProjects,
  } = useQuery<AutomationProjectSummary[], Error>({
    queryKey: ['automationProjects', selectedSubscriptionId],
    queryFn: async () => {
      return listAutomationProjects(selectedSubscriptionId, getArmToken);
    },
    enabled: !!selectedSubscriptionId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Build dropdown options. `SearchableCombobox` owns the type-to-filter and
  // selected-value-display behaviour, so we just declare the shape of each row.
  const subscriptionOptions = useMemo(() => {
    return (subscriptions ?? []).map((sub) => ({
      value: sub.subscriptionId,
      text: sub.displayName,
      secondaryText: sub.subscriptionId,
      searchTerms: [sub.subscriptionId],
    }));
  }, [subscriptions]);

  const resourceGroupOptions = useMemo(() => {
    return (resourceGroups ?? []).map((rg) => ({
      value: rg.name,
      text: rg.name,
    }));
  }, [resourceGroups]);

  const locationOptions = useMemo(() => {
    return getProvisioningLocationOptions(locations);
  }, [locations]);

  // Validate project name
  const existingProjectNames = useMemo(() => {
    return (existingProjects ?? []).map((p) => p.name);
  }, [existingProjects]);

  const projectNameValidation = useMemo(() => {
    return validateProjectName(projectName, existingProjectNames);
  }, [projectName, existingProjectNames]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedSubscriptionId('');
      setSelectedResourceGroup('');
      setSelectedLocation('');
      setProjectName('');
      setProvisioningState(null);
      setErrorMessage(null);
      setIsCreating(false);
    }
  }, [open]);

  // Reset dependent fields when subscription changes
  useEffect(() => {
    setSelectedResourceGroup('');
    setSelectedLocation('');
  }, [selectedSubscriptionId]);

  const handleClose = useCallback(() => {
    setSelectedSubscriptionId('');
    setSelectedResourceGroup('');
    setSelectedLocation('');
    setProjectName('');
    setProvisioningState(null);
    setErrorMessage(null);
    setIsCreating(false);
    onClose();
  }, [onClose]);

  const isFormValid = useMemo(() => {
    return (
      selectedSubscriptionId &&
      selectedResourceGroup &&
      selectedLocation &&
      projectNameValidation.isValid
    );
  }, [selectedSubscriptionId, selectedResourceGroup, selectedLocation, projectNameValidation.isValid]);

  // Compute validation messages as variables to satisfy exactOptionalPropertyTypes
  const subscriptionValidationState = subscriptionsError ? 'error' as const : 'none' as const;
  const subscriptionValidationMessage = subscriptionsError?.message ?? null;
  const resourceGroupValidationState = resourceGroupsError ? 'error' as const : 'none' as const;
  const resourceGroupValidationMessage = resourceGroupsError?.message ?? null;
  const locationValidationState = locationsError ? 'error' as const : 'none' as const;
  const locationValidationMessage = locationsError?.message ?? null;
  const projectNameValidationState = !isCreating && projectNameValidation.error ? 'error' as const : 'none' as const;
  const projectNameValidationMessage = !isCreating && projectNameValidation.error ? projectNameValidation.error : null;
  const projectNameHint = projectsLoading ? 'Checking for existing environments...' : null;

  // Error conditions for missing resources
  const hasNoSubscriptions = useMemo(() => !subscriptionsLoading && subscriptions && subscriptions.length === 0, [subscriptionsLoading, subscriptions]);
  const hasNoResourceGroups = useMemo(() => selectedSubscriptionId && !resourceGroupsLoading && resourceGroups && resourceGroups.length === 0, [selectedSubscriptionId, resourceGroupsLoading, resourceGroups]);
  const hasNoLocations = useMemo(() => selectedSubscriptionId && !locationsLoading && locations && locations.length === 0, [selectedSubscriptionId, locationsLoading, locations]);
  const hasBlockingError = useMemo(() => hasNoSubscriptions || hasNoResourceGroups || hasNoLocations, [hasNoSubscriptions, hasNoResourceGroups, hasNoLocations]);

  const handleCreateProject = useCallback(async () => {
    if (!isFormValid) return;

    setIsCreating(true);
    setProvisioningState('Creating...');
    setErrorMessage(null);

    const trimmedName = projectName.trim();

    try {
      const result = await createAutomationProject(
        selectedSubscriptionId,
        selectedResourceGroup,
        trimmedName,
        selectedLocation,
        getArmToken
      );

      const state = result.properties?.provisioningState ?? 'Succeeded';
      setProvisioningState(state);

      if (state === 'Succeeded' || state === 'Accepted') {
        // Invalidate project queries to refresh the list
        await refetchProjects();
        addNotification({
          type: 'success',
          title: 'Environment created',
          message: `Environment "${trimmedName}" has been created successfully.`,
        });
        onProjectCreated?.();
        handleClose();
      } else if (state === 'Failed' || state === 'Canceled') {
        setErrorMessage(`Environment provisioning ${state.toLowerCase()}.`);
        addNotification({
          type: 'error',
          title: 'Environment creation failed',
          message: `Environment provisioning ${state.toLowerCase()}.`,
        });
        setIsCreating(false);
      } else {
        // Non-terminal provisioning state (e.g. `Accepted`, `Provisioning`).
        // Add the project to the list immediately; `useProjects` /
        // `useProjectsMultiRegion` will poll every 5s until ARM settles
        // the state to Succeeded/Failed/Canceled — no manual refresh
        // needed (issue #1645).
        refetchProjects();
        addNotification({
          type: 'info',
          title: 'Environment provisioning',
          message: `Environment "${trimmedName}" is provisioning. Status will update automatically once Azure finishes setting it up.`,
        });
        onProjectCreated?.();
        handleClose();
      }
    } catch (error) {
      console.error('Error creating environment:', error);
      const message = error instanceof Error ? error.message : 'Failed to create environment';
      addNotification({
        type: 'error',
        title: 'Environment creation failed',
        message,
      });
      setErrorMessage(message);
      setProvisioningState('Failed');
      setIsCreating(false);
    }
  }, [isFormValid, projectName, selectedSubscriptionId, selectedResourceGroup, selectedLocation, getArmToken, refetchProjects, addNotification, onProjectCreated, handleClose]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle>Create an environment</DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {!hasBlockingError && (
              <MessageBar className={styles.infoBar} intent="info" icon={<InfoRegular />}>
                <MessageBarBody>
                  Create the environment in{' '}
                  <Link href={CREATE_PROJECT_URL} target="_blank" rel="noopener noreferrer" inline>
                    Azure
                  </Link>{' '}
                  if you need advanced options.
                </MessageBarBody>
              </MessageBar>
            )}

            {/* Error: No subscriptions available */}
            {hasNoSubscriptions && (
              <MessageBar className={styles.infoBar} intent="error">
                <MessageBarBody>
                  You do not have any subscriptions available to create an environment. Please get access to an Azure subscription to proceed.
                </MessageBarBody>
              </MessageBar>
            )}

            {/* Error: No resource groups after subscription selection */}
            {(hasNoResourceGroups && !hasNoLocations) && (
              <MessageBar className={styles.infoBar} intent="error">
                <MessageBarBody>
                  Please use{' '}
                  <Link href="https://portal.azure.com/#create/Microsoft.ResourceGroup" target="_blank" rel="noopener noreferrer" inline>
                    Azure Portal
                  </Link>{' '}
                  to create a resource group and continue environment creation after.
                </MessageBarBody>
              </MessageBar>
            )}

            {/* Error: No locations after subscription selection */}
            {hasNoLocations && (
              <MessageBar className={styles.infoBar} intent="error">
                <MessageBarBody>
                  Your subscription is not enabled for environments. Please contact Support to enable environments for this subscription.
                </MessageBarBody>
              </MessageBar>
            )}

            <div className={styles.formContainer}>
              {/* Subscription selector */}
              <Field
                label="Subscription"
                required
                validationState={subscriptionValidationState}
                validationMessage={subscriptionValidationMessage}
              >
                <div className={styles.fieldRow}>
                  <SearchableCombobox
                    className={styles.fieldControl}
                    options={subscriptionOptions}
                    value={selectedSubscriptionId || undefined}
                    onChange={setSelectedSubscriptionId}
                    disabled={subscriptionsLoading || isCreating}
                    placeholder={subscriptionsLoading ? 'Loading subscriptions...' : 'Search or select a subscription'}
                    aria-label="Subscription"
                    noMatchMessage="No matching subscriptions"
                  />
                  <Button
                    className={styles.refreshButton}
                    appearance="subtle"
                    icon={subscriptionsLoading ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
                    onClick={() => refetchSubscriptions()}
                    disabled={subscriptionsLoading || isCreating}
                    aria-label="Refresh subscriptions"
                    title="Refresh subscriptions"
                  />
                </div>
              </Field>

              {/* Resource group selector */}
              <Field
                label="Resource group"
                required
                validationState={resourceGroupValidationState}
                validationMessage={resourceGroupValidationMessage}
              >
                <div className={styles.fieldRow}>
                  <SearchableCombobox
                    className={styles.fieldControl}
                    options={resourceGroupOptions}
                    value={selectedResourceGroup || undefined}
                    onChange={setSelectedResourceGroup}
                    disabled={!selectedSubscriptionId || resourceGroupsLoading || isCreating}
                    placeholder={
                      !selectedSubscriptionId
                        ? 'Select a subscription first'
                        : resourceGroupsLoading
                          ? 'Loading resource groups...'
                          : 'Search or select a resource group'
                    }
                    aria-label="Resource group"
                    noMatchMessage="No matching resource groups"
                  />
                  <Button
                    className={styles.refreshButton}
                    appearance="subtle"
                    icon={resourceGroupsLoading ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
                    onClick={() => refetchResourceGroups()}
                    disabled={!selectedSubscriptionId || resourceGroupsLoading || isCreating}
                    aria-label="Refresh resource groups"
                    title="Refresh resource groups"
                  />
                </div>
              </Field>

              {/* Region selector */}
              <Field
                label="Region"
                required
                validationState={locationValidationState}
                validationMessage={locationValidationMessage}
              >
                <div className={styles.fieldRow}>
                  <SearchableCombobox
                    className={styles.fieldControl}
                    options={locationOptions}
                    value={selectedLocation || undefined}
                    onChange={setSelectedLocation}
                    disabled={!selectedSubscriptionId || locationsLoading || isCreating}
                    placeholder={
                      !selectedSubscriptionId
                        ? 'Select a subscription first'
                        : locationsLoading
                          ? 'Loading regions...'
                          : 'Search or select a region'
                    }
                    aria-label="Region"
                    noMatchMessage="No matching regions"
                  />
                  <Button
                    className={styles.refreshButton}
                    appearance="subtle"
                    icon={locationsLoading ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
                    onClick={() => refetchLocations()}
                    disabled={!selectedSubscriptionId || locationsLoading || isCreating}
                    aria-label="Refresh regions"
                    title="Refresh regions"
                  />
                </div>
              </Field>

              {/* Project name */}
              <Field
                label="Name"
                required
                validationState={projectNameValidationState}
                validationMessage={projectNameValidationMessage}
                hint={projectNameHint}
              >
                <div className={styles.fieldRow}>
                  <Input
                    className={styles.fieldControl}
                    value={projectName}
                    onChange={(_, data) => setProjectName(data.value)}
                    placeholder={!selectedSubscriptionId ? 'Select a subscription first' : 'Enter environment name'}
                    disabled={!selectedSubscriptionId || isCreating}
                    autoComplete="off"
                  />
                  <div className={styles.refreshButtonSpacer} aria-hidden="true" />
                </div>
              </Field>

              {/* Status indicator */}
              {provisioningState && (
                <div className={styles.statusContainer}>
                  {isCreating && <ProgressBar />}
                  <Text className={styles.statusText}>Status: {provisioningState}</Text>
                </div>
              )}

              {/* Error message */}
              {errorMessage && (
                <div className={styles.errorContainer}>
                  <Text className={styles.errorText}>{errorMessage}</Text>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button appearance="secondary" onClick={handleClose}>
              {isCreating ? 'Close' : 'Cancel'}
            </Button>
            <Button
              appearance="primary"
              onClick={handleCreateProject}
              disabled={!isFormValid || isCreating || subscriptionsLoading || resourceGroupsLoading || locationsLoading || projectsLoading}
            >
              Create
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

CreateProjectDialog.displayName = 'CreateProjectDialog';
